import { useCallback } from 'react'
import { useLocation } from 'react-router-dom'
import { useAuthentication } from '@auth/hooks/useAuthentication'
import { useModal } from '@hooks/useModal'
import { useSnackbar } from '@contexts/SnackbarContext'
import { useTrackEvent } from '@analytics/trackEvent'
import type { LogoutEvent } from '@analytics/types'
import { normalizePathnameForAnalytics } from '@analytics/pathUtils'

/**
 * `authService.logout()` redirects to Okta's signout endpoint and then awaits a never-resolving
 * promise, so anything after `await logout()` is unreachable. Post-redirect UX (snackbar,
 * navigation) is owned by `LogoutCallback`. Catch blocks only fire on pre-await throws.
 *
 * LOGOUT analytics is dispatched here synchronously BEFORE `await logout()` (same pattern as
 * `LOGIN_ATTEMPT` before OAuth redirect): GTM's GA4 tag uses `sendBeacon` so the hit survives
 * the redirect, and we still have full account SDR globals in `useTrackEvent`'s snapshot.
 */

export const useLogout = () => {
  const location = useLocation()
  const { logout } = useAuthentication()
  const { closeModal, closeAllModals } = useModal({ modalID: 'logout-modal' })
  const { showSnackbar } = useSnackbar()
  const trackEvent = useTrackEvent()

  const handleLogout = useCallback(async () => {
    trackEvent<LogoutEvent>({
      type: 'LOGOUT',
      payload: { custom_page_name: normalizePathnameForAnalytics(location.pathname) },
    })

    try {
      // Close modals before logout starts so SignIn components don't mount mid-tear-down.
      closeAllModals?.()
      closeModal?.()

      if (!logout || typeof logout !== 'function') {
        throw new Error('Logout function not available from authentication context')
      }
      await logout()
    } catch (error) {
      console.error('Logout failed:', error)
      showSnackbar({
        message: 'Logout failed. Please try again.',
        type: 'error',
        duration: 3,
      })
    }
  }, [logout, location.pathname, closeModal, closeAllModals, showSnackbar, trackEvent])

  return { handleLogout }
}

// Variant safe to use before `SnackbarProvider` is mounted (idle-timeout path).
export const useTimeoutLogout = () => {
  const location = useLocation()
  const { logout } = useAuthentication()
  const { closeModal, closeAllModals } = useModal({ modalID: 'logout-modal' })
  const trackEvent = useTrackEvent()

  const handleTimeoutLogout = useCallback(async () => {
    trackEvent<LogoutEvent>({
      type: 'LOGOUT',
      payload: { custom_page_name: normalizePathnameForAnalytics(location.pathname) },
    })

    try {
      closeAllModals?.()
      closeModal?.()

      if (!logout || typeof logout !== 'function') {
        throw new Error('Logout function not available from authentication context')
      }
      await logout()
    } catch (error) {
      console.error('Timeout logout failed:', error)
    }
  }, [logout, location.pathname, closeModal, closeAllModals, trackEvent])

  return { handleTimeoutLogout }
}
